import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Lock, Eye, Shield, Database, UserCheck, Mail } from 'lucide-react';

const Privacy = () => {
  const { themeColors } = useTheme();

  const containerStyle = {
    minHeight: 'calc(100vh - 120px)',
    backgroundColor: themeColors.background,
    padding: '40px 20px'
  };

  const contentStyle = {
    maxWidth: '900px',
    margin: '0 auto'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '60px'
  };

  const titleStyle = {
    color: themeColors.text,
    fontSize: '48px',
    fontWeight: 'bold',
    marginBottom: '20px'
  };

  const subtitleStyle = {
    color: themeColors.textSecondary,
    fontSize: '18px',
    lineHeight: '1.6',
    maxWidth: '700px',
    margin: '0 auto'
  };

  const lastUpdatedStyle = {
    color: themeColors.textSecondary,
    fontSize: '14px',
    fontStyle: 'italic',
    marginTop: '12px'
  };

  const sectionStyle = {
    marginBottom: '48px'
  };

  const sectionTitleStyle = {
    color: themeColors.text,
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  };

  const paragraphStyle = {
    color: themeColors.textSecondary,
    fontSize: '16px',
    lineHeight: '1.8',
    marginBottom: '16px'
  };

  const listStyle = {
    color: themeColors.textSecondary,
    fontSize: '16px',
    lineHeight: '2',
    paddingLeft: '20px',
    marginBottom: '16px'
  };

  const subsectionTitleStyle = {
    color: themeColors.text,
    fontSize: '18px',
    fontWeight: '600',
    marginTop: '24px',
    marginBottom: '12px'
  };

  const linkStyle = {
    color: themeColors.primary,
    textDecoration: 'none',
    fontWeight: '500'
  };

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h1 style={titleStyle}>Privacy Policy</h1>
          <p style={subtitleStyle}>
            Your privacy is important to us. This policy explains how we collect, use, and protect your information.
          </p>
          <p style={lastUpdatedStyle}>Last updated: March 2026</p>
        </div>

        {/* Introduction */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>
            <Lock size={28} color={themeColors.primary} />
            Our Commitment to Privacy
          </h2>
          <p style={paragraphStyle}>
            BabyResell, operated by Interverse ("we," "us," or "our"), is committed to protecting your privacy. This Privacy Policy explains how we collect, use, share, and protect your personal information when you use our Platform.
          </p>
          <p style={paragraphStyle}>
            By using BabyResell, you agree to the collection and use of information in accordance with this policy.
          </p>
        </div>

        {/* Information We Collect */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>
            <Database size={28} color={themeColors.primary} />
            Information We Collect
          </h2>

          <h3 style={subsectionTitleStyle}>Information You Provide:</h3>
          <ul style={listStyle}>
            <li><strong>Account Information:</strong> Username, email address, password, profile photo, and bio</li>
            <li><strong>Profile Information:</strong> Location, bio, and preferences</li>
            <li><strong>Listings:</strong> Photos, descriptions, pricing, and categories for items you list</li>
            <li><strong>Payment Information:</strong> Payment details processed securely through Stripe (we do not store credit card numbers)</li>
            <li><strong>Communications:</strong> Messages sent through our platform, support requests, and feedback</li>
            <li><strong>Transaction Data:</strong> Purchase history, sales history, reviews, and ratings</li>
          </ul>

          <h3 style={subsectionTitleStyle}>Information We Collect Automatically:</h3>
          <ul style={listStyle}>
            <li><strong>Usage Data:</strong> Pages visited, features used, time spent on the platform</li>
            <li><strong>Device Information:</strong> Browser type, operating system, IP address, device identifiers</li>
            <li><strong>Cookies:</strong> We use cookies to enhance your experience (see our <a href="/cookies" style={linkStyle}>Cookie Policy</a>)</li>
            <li><strong>Location Data:</strong> Approximate location based on IP address (with your consent)</li>
          </ul>
        </div>

        {/* How We Use Your Information */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>
            <Eye size={28} color={themeColors.primary} />
            How We Use Your Information
          </h2>
          <p style={paragraphStyle}>We use your information to:</p>
          <ul style={listStyle}>
            <li>Create and manage your account</li>
            <li>Facilitate buying and selling transactions</li>
            <li>Process payments and payouts</li>
            <li>Communicate with you about orders, updates, and support</li>
            <li>Provide customer support and resolve disputes</li>
            <li>Improve and personalize your experience</li>
            <li>Detect and prevent fraud, abuse, and security issues</li>
            <li>Analyze usage patterns and improve our platform</li>
            <li>Send marketing communications (with your consent)</li>
            <li>Comply with legal obligations</li>
          </ul>
        </div>

        {/* How We Share Your Information */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>
            <UserCheck size={28} color={themeColors.primary} />
            How We Share Your Information
          </h2>
          <p style={paragraphStyle}>We may share your information with:</p>

          <h3 style={subsectionTitleStyle}>Other Users:</h3>
          <ul style={listStyle}>
            <li>Your public profile (username, photo, bio) is visible to all users</li>
            <li>Buyers and sellers in a transaction can see each other's usernames and necessary shipping information</li>
            <li>Your reviews and ratings are publicly visible</li>
          </ul>

          <h3 style={subsectionTitleStyle}>Service Providers:</h3>
          <ul style={listStyle}>
            <li><strong>Stripe:</strong> Payment processing and seller payouts</li>
            <li><strong>Cloudinary:</strong> Image hosting and optimization</li>
            <li><strong>Email Services:</strong> Transaction notifications and communications</li>
            <li><strong>Analytics Providers:</strong> Understanding usage patterns (data is anonymized)</li>
          </ul>

          <h3 style={subsectionTitleStyle}>Legal Requirements:</h3>
          <p style={paragraphStyle}>
            We may disclose your information if required by law, legal process, or to protect the rights, property, or safety of BabyResell, our users, or the public.
          </p>

          <h3 style={subsectionTitleStyle}>Business Transfers:</h3>
          <p style={paragraphStyle}>
            In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.
          </p>
        </div>

        {/* Data Security */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>
            <Shield size={28} color={themeColors.primary} />
            Data Security
          </h2>
          <p style={paragraphStyle}>
            We implement industry-standard security measures to protect your information:
          </p>
          <ul style={listStyle}>
            <li>Encrypted data transmission (HTTPS/SSL)</li>
            <li>Secure password hashing</li>
            <li>Regular security audits and updates</li>
            <li>Access controls and authentication</li>
            <li>PCI DSS compliant payment processing through Stripe</li>
          </ul>
          <p style={paragraphStyle}>
            However, no method of transmission over the internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.
          </p>
        </div>

        {/* Data Retention */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Data Retention</h2>
          <p style={paragraphStyle}>
            We retain your personal information for as long as necessary to:
          </p>
          <ul style={listStyle}>
            <li>Provide our services to you</li>
            <li>Comply with legal obligations</li>
            <li>Resolve disputes and enforce agreements</li>
            <li>Maintain transaction records for accounting and tax purposes</li>
          </ul>
          <p style={paragraphStyle}>
            If you delete your account, we will delete or anonymize your personal information within 30 days, except where we are required by law to retain it.
          </p>
        </div>

        {/* Your Rights */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Your Privacy Rights</h2>
          <p style={paragraphStyle}>You have the right to:</p>
          <ul style={listStyle}>
            <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your account and personal data</li>
            <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails at any time</li>
            <li><strong>Data Portability:</strong> Request your data in a machine-readable format</li>
            <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing where applicable</li>
          </ul>
          <p style={paragraphStyle}>
            To exercise these rights, please contact us at{' '}
            <a href="mailto:privacy@interverse.app" style={linkStyle}>
              privacy@interverse.app
            </a>
            .
          </p>
        </div>

        {/* Children's Privacy */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Children's Privacy</h2>
          <p style={paragraphStyle}>
            BabyResell is not intended for use by children under 18. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us immediately.
          </p>
        </div>

        {/* Cookies */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Cookies & Tracking</h2>
          <p style={paragraphStyle}>
            We use cookies and similar technologies to enhance your experience, analyze usage, and provide personalized features. For more information, please see our{' '}
            <a href="/cookies" style={linkStyle}>
              Cookie Policy
            </a>
            .
          </p>
          <p style={paragraphStyle}>
            You can manage cookie preferences through your browser settings.
          </p>
        </div>

        {/* Third-Party Links */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Third-Party Links</h2>
          <p style={paragraphStyle}>
            Our Platform may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
          </p>
        </div>

        {/* California Privacy Rights */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>California Privacy Rights (CCPA)</h2>
          <p style={paragraphStyle}>
            If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
          </p>
          <ul style={listStyle}>
            <li>Right to know what personal information we collect, use, and share</li>
            <li>Right to delete your personal information</li>
            <li>Right to opt-out of the sale of your personal information (we do not sell personal data)</li>
            <li>Right to non-discrimination for exercising your privacy rights</li>
          </ul>
        </div>

        {/* Changes to Privacy Policy */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Changes to This Privacy Policy</h2>
          <p style={paragraphStyle}>
            We may update this Privacy Policy from time to time. When we make material changes, we will notify you via email or through a notice on the Platform. The "Last updated" date at the top will reflect the most recent revision.
          </p>
          <p style={paragraphStyle}>
            Continued use of BabyResell after changes are posted constitutes your acceptance of the updated policy.
          </p>
        </div>

        {/* Contact */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>
            <Mail size={28} color={themeColors.primary} />
            Contact Us
          </h2>
          <p style={paragraphStyle}>
            If you have questions or concerns about this Privacy Policy or how we handle your data, please contact us:
          </p>
          <ul style={{ ...listStyle, listStyle: 'none', paddingLeft: 0 }}>
            <li>
              Email:{' '}
              <a href="mailto:privacy@interverse.app" style={linkStyle}>
                privacy@interverse.app
              </a>
            </li>
            <li>
              Support:{' '}
              <a href="/contact" style={linkStyle}>
                Contact Support
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
